import convert, { Mass, Unit, Volume } from 'convert-units';
import { WasteMeasurement } from './schema';
import { getOneLink } from './utils';

// Conversion factors for volume to weight
const GALLONS_PER_CUBIC_YARD = 201.974;

type TargetUnit = Volume | Mass;

interface ConversionOptions {
    materialBreakdown?: boolean;
    itemIds?: string[];
    datasetIds?: string[];
    targetUnit?: TargetUnit;
}

/**
 * Converts all waste measurements to a common unit (default: 'lb')
 * and filters based on provided options
 */
export function convertAndFilterMeasurements(measurements: WasteMeasurement[], options: ConversionOptions = {}): WasteMeasurement[] {
    const { materialBreakdown = true, itemIds = [], datasetIds = [], targetUnit = 'lb' } = options;

    // Apply filters
    const filteredByDataset = filterByDatasetIds(measurements, datasetIds);
    const filteredByItems = filterByItemIds(filteredByDataset, itemIds, materialBreakdown);

    // Convert measurements to target unit
    return convertMeasurementsToUnit(filteredByItems, targetUnit);
}

/**
 * Filters measurements by dataset IDs
 */
function filterByDatasetIds(measurements: WasteMeasurement[], datasetIds: string[]): WasteMeasurement[] {
    if (datasetIds.length === 0) {
        return measurements;
    }

    return measurements.filter(item => {
        const dataset = getOneLink(item?.dataset);
        return dataset && datasetIds.includes(dataset.id);
    });
}

/**
 * Filters measurements by material or category IDs
 */
function filterByItemIds(measurements: WasteMeasurement[], itemIds: string[], materialBreakdown: boolean): WasteMeasurement[] {
    if (itemIds.length === 0) {
        return measurements;
    }

    if (materialBreakdown) {
        // Filter by material IDs
        return measurements.filter(item => {
            const material = getOneLink(item?.material);
            return material && itemIds.includes(material.id);
        });
    } else {
        // Filter by category IDs
        return measurements.filter(item => {
            const material = getOneLink(item?.material);
            const category = getOneLink(material?.category);
            return category && itemIds.includes(category.id);
        });
    }
}

/**
 * Converts all measurements to the specified target unit
 */
function convertMeasurementsToUnit(measurements: WasteMeasurement[], targetUnit: TargetUnit): WasteMeasurement[] {
    return measurements.map(measurement => convertMeasurement(measurement, targetUnit)).filter(Boolean) as WasteMeasurement[]; // Remove null entries
}

/**
 * Converts a single measurement to the target unit
 */
function convertMeasurement(measurement: WasteMeasurement, targetUnit: TargetUnit): WasteMeasurement | null {
    const material = getOneLink(measurement?.material);
    const { quantity, units } = measurement;

    // If already in target unit, no conversion needed
    if (units === targetUnit) {
        return { ...measurement };
    }

    try {
        const customUnitsList = material?.customUnits as Array<{ unit: string; lbsPerUnit: number }> | undefined;
        const convertedQuantity = performUnitConversion(
            quantity,
            units,
            targetUnit,
            material?.lbsPerYd3,
            Array.isArray(customUnitsList) ? customUnitsList : undefined
        );

        if (convertedQuantity === null) {
            return null;
        }

        return {
            ...measurement,
            quantity: convertedQuantity,
            units: targetUnit,
        };
    } catch (error) {
        console.error(`Conversion error for ${quantity} ${units} to ${targetUnit}:`, error);
        return null;
    }
}

type CustomUnitEntry = { unit: string; lbsPerUnit: number };

/**
 * Performs the actual unit conversion based on unit types
 */
function performUnitConversion(
    quantity: number,
    fromUnit: Unit | string,
    toUnit: Unit,
    density?: number,
    customUnits?: CustomUnitEntry[]
): number | null {
    // Handle material-defined custom input units (multiple per material) -> lb (then optionally to volume)
    const customEntry = customUnits?.find(cu => cu.unit === fromUnit);
    if (customEntry != null && customEntry.lbsPerUnit != null) {
        const quantityInLb = quantity * customEntry.lbsPerUnit;
        if (toUnit === 'lb') return quantityInLb;
        if (isVolumeUnit(toUnit) && density != null) {
            return convertMassToVolume(quantityInLb, 'lb', toUnit, density);
        }
        if (isMassUnit(toUnit)) {
            return convert(quantityInLb).from('lb').to(toUnit);
        }
        return null;
    }

    // Handle mass to mass conversion
    if (isMassUnit(fromUnit as Unit) && isMassUnit(toUnit)) {
        return convert(quantity)
            .from(fromUnit as Unit)
            .to(toUnit);
    }

    // Handle volume to volume conversion
    if (isVolumeUnit(fromUnit as Unit) && isVolumeUnit(toUnit)) {
        return convert(quantity)
            .from(fromUnit as Unit)
            .to(toUnit);
    }

    // Handle volume to mass conversion (requires density)
    if (isVolumeUnit(fromUnit as Unit) && isMassUnit(toUnit)) {
        if (!density) return null;
        return convertVolumeToMass(quantity, fromUnit as string, density);
    }

    // Handle mass to volume conversion (requires density)
    if (isMassUnit(fromUnit as Unit) && isVolumeUnit(toUnit)) {
        if (!density) return null;
        return convertMassToVolume(quantity, fromUnit as string, toUnit, density);
    }

    // Unsupported conversion
    return null;
}

/**
 * Converts volume to mass using material density
 */
function convertVolumeToMass(quantity: number, volumeUnit: string, density: number): number {
    // Convert to cubic yards first
    let volumeInCubicYards: number;

    if (volumeUnit === 'yd3') {
        volumeInCubicYards = quantity;
    } else if (volumeUnit === 'gal') {
        volumeInCubicYards = quantity / GALLONS_PER_CUBIC_YARD;
    } else {
        // Convert other volume units to cubic yards
        volumeInCubicYards = convert(quantity)
            .from(volumeUnit as any)
            .to('yd3' as any);
    }

    // Apply density (lbs/yd³) to get weight
    return volumeInCubicYards * density;
}

/**
 * Converts mass to volume using material density
 */
function convertMassToVolume(quantity: number, massUnit: string, targetVolumeUnit: string, density: number): number {
    // Convert to lbs first
    const weightInLbs =
        massUnit === 'lb'
            ? quantity
            : convert(quantity)
                  .from(massUnit as any)
                  .to('lb' as any);

    // Apply density (lbs/yd³) to get volume in cubic yards
    const volumeInCubicYards = weightInLbs / density;

    // Convert to target volume unit if needed
    if (targetVolumeUnit === 'yd3') {
        return volumeInCubicYards;
    } else if (targetVolumeUnit === 'gal') {
        return volumeInCubicYards * GALLONS_PER_CUBIC_YARD;
    } else {
        // Convert cubic yards to target volume unit
        return convert(volumeInCubicYards)
            .from('yd3' as any)
            .to(targetVolumeUnit as any);
    }
}

// Helper functions to check unit types
function isMassUnit(unit: Unit): boolean {
    return convert().possibilities('mass').includes(unit);
}

function isVolumeUnit(unit: Unit): boolean {
    return convert().possibilities('volume').includes(unit);
}
